<template>
  <div class="page">
    <el-card class="card">
      <div slot="header" class="card-header">
        <span>过磅审核</span>
      </div>

      <!-- 查询区 -->
      <el-form :inline="true" :model="query" class="query-form">
        <el-form-item label="关键字">
          <el-input
            v-model="query.keyword"
            placeholder="过磅单号/车辆/车牌/小区"
            clearable
            style="width:260px;"
            @keyup.enter.native="fetchList"
          />
        </el-form-item>

        <el-form-item label="审核状态">
          <el-select v-model="query.audit_status" placeholder="全部" clearable style="width:140px;">
            <el-option label="待审核" :value="0" />
            <el-option label="已通过" :value="1" />
            <el-option label="已驳回" :value="2" />
          </el-select>
        </el-form-item>

        <el-form-item>
          <el-button type="primary" icon="el-icon-search" @click="fetchList">查询</el-button>
          <el-button icon="el-icon-refresh" @click="reset">重置</el-button>
        </el-form-item>
      </el-form>

      <!-- 表格区 -->
      <div class="table-wrap">
        <el-table
          :data="list"
          border
          stripe
          v-loading="loading"
          style="width:100%;"
          height="100%"
        >
          <el-table-column prop="weigh_id" label="过磅ID" width="90" />
          <el-table-column prop="weigh_no" label="过磅单号" min-width="220" />
          <el-table-column prop="vehicle_name" label="车辆名称" min-width="140" />
          <el-table-column prop="plate_no" label="车牌号" width="120" />
          <el-table-column label="垃圾类型" width="100">
            <template slot-scope="s">
              {{ s.row.garbage_name || garbageText(s.row.garbage_type) }}
            </template>
          </el-table-column>
          <el-table-column label="实际过磅重(kg)" width="120">
            <template slot-scope="s">{{ num2(s.row.weight) }}</template>
          </el-table-column>
          <el-table-column label="快照理论重(kg)" width="120">
            <template slot-scope="s">{{ num2(s.row.snapshot_room_weight || s.row.room_weight) }}</template>
          </el-table-column>
          <el-table-column label="实时理论重(kg)" width="120">
            <template slot-scope="s">{{ num2(s.row.final_room_weight) }}</template>
          </el-table-column>

          <el-table-column label="审核状态" width="100">
            <template slot-scope="s">
              <el-tag :type="auditTagType(s.row.audit_status)">
                {{ auditText(s.row.audit_status) }}
              </el-tag>
            </template>
          </el-table-column>

          <el-table-column label="提交时间" width="170">
            <template slot-scope="s">
              {{ formatTime(s.row.created_at || s.row.create_time || s.row.submit_time) }}
            </template>
          </el-table-column>

          <el-table-column label="操作" width="140" align="center">
            <template slot-scope="s">
              <el-button type="text" @click="openDetail(s.row)">详情</el-button>

              <el-button
                v-if="Number(s.row.audit_status) === 0"
                type="text"
                @click="openEdit(s.row)"
              >
                审核
              </el-button>
            </template>
          </el-table-column>
        </el-table>
      </div>

      <div class="pager">
        <el-pagination
          background
          layout="total, prev, pager, next, sizes"
          :total="total"
          :current-page="page"
          :page-size="pageSize"
          @current-change="p => { page = p; fetchList() }"
          @size-change="s => { pageSize = s; page = 1; fetchList() }"
        />
      </div>
    </el-card>

    <!-- 详情弹窗 -->
    <el-dialog title="过磅单详情" :visible.sync="detailVisible" width="1180px">
      <div v-if="previewLoading" class="center-loading">
        <i class="el-icon-loading"></i>
        <span>正在加载详情...</span>
      </div>

      <div v-else-if="detailPreview">
        <div class="section-title">总信息</div>

        <el-descriptions :column="3" border>
          <el-descriptions-item label="过磅单ID">{{ detailPreview.weigh_id }}</el-descriptions-item>
          <el-descriptions-item label="过磅单号">{{ detailPreview.weigh_no }}</el-descriptions-item>
          <el-descriptions-item label="车辆ID">{{ detailPreview.vehicle_id }}</el-descriptions-item>

          <el-descriptions-item label="垃圾类型">
            {{ detailPreview.garbage_name || garbageText(detailPreview.garbage_type) }}
          </el-descriptions-item>
          <el-descriptions-item label="区间开始">{{ formatTime(detailPreview.start_time) }}</el-descriptions-item>
          <el-descriptions-item label="区间结束">{{ formatTime(detailPreview.end_time) }}</el-descriptions-item>

          <el-descriptions-item label="实际过磅重量">{{ num2(detailPreview.weight) }} kg</el-descriptions-item>
          <el-descriptions-item label="提交时快照重量">{{ num2(detailPreview.snapshot_room_weight) }} kg</el-descriptions-item>
          <el-descriptions-item label="审核实时重量">{{ num2(detailPreview.final_room_weight) }} kg</el-descriptions-item>

          <el-descriptions-item label="审核实时误差">
            <span :class="{ danger: Number(detailPreview.final_diff_weight || 0) !== 0 }">
              {{ num2(detailPreview.final_diff_weight) }} kg
            </span>
          </el-descriptions-item>
          <el-descriptions-item label="审核实时误差率">
            <span :class="{ danger: getFinalDiffRatePercent(detailPreview) > 5 }">
              {{ getFinalDiffRatePercent(detailPreview) }}%
            </span>
          </el-descriptions-item>
          <el-descriptions-item label="存在未审核订单">
            <el-tag :type="detailPreview.has_unaudited_orders ? 'danger' : 'success'">
              {{ detailPreview.has_unaudited_orders ? '是' : '否' }}
            </el-tag>
          </el-descriptions-item>
        </el-descriptions>

        <div
          v-if="detailPreview.has_unaudited_orders && detailPreview.unaudited_order_nos && detailPreview.unaudited_order_nos.length"
          class="warn-box"
        >
          <div class="warn-title">当前区间存在未审核订单：</div>
          <div class="warn-tags">
            <el-tag
              v-for="no in detailPreview.unaudited_order_nos"
              :key="no"
              type="danger"
              size="small"
              class="mb6 mr6"
            >
              {{ no }}
            </el-tag>
          </div>
        </div>

        <div class="section-title">设备明细</div>

        <el-table :data="detailPreview.detail_items || []" border stripe>
          <el-table-column prop="community_name" label="小区名称" width="150" />
          <el-table-column prop="device_code" label="设备编码" width="120" />
          <el-table-column label="快照重量(kg)" width="120">
            <template slot-scope="s">{{ num2(s.row.snapshot_weight) }}</template>
          </el-table-column>
          <el-table-column label="实时有效重量(kg)" width="130">
            <template slot-scope="s">{{ num2(s.row.order_weight) }}</template>
          </el-table-column>
          <el-table-column label="实际确认重量(kg)" width="130">
            <template slot-scope="s">{{ num2(s.row.actual_weight) }}</template>
          </el-table-column>
          <el-table-column label="误差(kg)" width="100">
            <template slot-scope="s">
              <span :class="{ danger: Number(s.row.diff_weight || 0) !== 0 }">
                {{ num2(s.row.diff_weight) }}
              </span>
            </template>
          </el-table-column>
          <el-table-column prop="unaudited_order_count" label="未审核订单数" width="110" />
          <el-table-column label="未审核订单号" min-width="220">
            <template slot-scope="s">
              <span v-if="!s.row.unaudited_order_nos || !s.row.unaudited_order_nos.length">-</span>
              <el-tag
                v-for="no in s.row.unaudited_order_nos"
                :key="no"
                type="warning"
                size="mini"
                class="mb6 mr6"
              >
                {{ no }}
              </el-tag>
            </template>
          </el-table-column>
        </el-table>
      </div>

      <span slot="footer">
        <el-button @click="detailVisible=false">关闭</el-button>
      </span>
    </el-dialog>

    <!-- 修改弹窗 -->
    <el-dialog title="修改审核明细" :visible.sync="editVisible" width="1200px">
      <div v-if="previewLoading" class="center-loading">
        <i class="el-icon-loading"></i>
        <span>正在加载审核预览...</span>
      </div>

      <div v-else-if="editPreview">
        <el-alert
          :title="editAlertText"
          :type="needManual(editPreview) ? 'warning' : 'success'"
          :closable="false"
          show-icon
          style="margin-bottom:14px;"
        />

        <div
          v-if="editPreview.has_unaudited_orders"
          class="warn-box"
        >
          <div class="warn-title">当前区间存在未审核订单，暂时不能通过：</div>
          <div class="warn-tags">
            <el-tag
              v-for="no in editPreview.unaudited_order_nos"
              :key="no"
              type="danger"
              size="small"
              class="mb6 mr6"
            >
              {{ no }}
            </el-tag>
          </div>
        </div>

        <div class="section-title">总信息</div>

        <el-descriptions :column="3" border>
          <el-descriptions-item label="过磅单号">{{ editPreview.weigh_no }}</el-descriptions-item>
          <el-descriptions-item label="垃圾类型">
            {{ editPreview.garbage_name || garbageText(editPreview.garbage_type) }}
          </el-descriptions-item>
          <el-descriptions-item label="车辆ID">{{ editPreview.vehicle_id }}</el-descriptions-item>

          <el-descriptions-item label="实际过磅重量">{{ num2(editPreview.weight) }} kg</el-descriptions-item>
          <el-descriptions-item label="提交时快照重量">{{ num2(editPreview.snapshot_room_weight) }} kg</el-descriptions-item>
          <el-descriptions-item label="审核实时重量">{{ num2(editPreview.final_room_weight) }} kg</el-descriptions-item>

          <el-descriptions-item label="审核实时误差">
            <span :class="{ danger: Number(editPreview.final_diff_weight || 0) !== 0 }">
              {{ num2(editPreview.final_diff_weight) }} kg
            </span>
          </el-descriptions-item>
          <el-descriptions-item label="审核实时误差率">
            <span :class="{ danger: getFinalDiffRatePercent(editPreview) > 5 }">
              {{ getFinalDiffRatePercent(editPreview) }}%
            </span>
          </el-descriptions-item>
          <el-descriptions-item label="审核规则">
            {{ needManual(editPreview) ? '需要人工调整' : '可自动通过' }}
          </el-descriptions-item>
        </el-descriptions>

        <div class="section-title">审核备注</div>
        <el-input
          v-model="editForm.remark"
          type="textarea"
          :rows="3"
          placeholder="审核备注 / 人工调整说明"
        />

        <div class="section-title">设备明细</div>

        <el-table :data="editForm.detail_items" border stripe>
          <el-table-column prop="community_name" label="小区名称" width="150" />
          <el-table-column prop="device_code" label="设备编码" width="120" />
          <el-table-column label="快照重量(kg)" width="120">
            <template slot-scope="s">{{ num2(s.row.snapshot_weight) }}</template>
          </el-table-column>
          <el-table-column label="实时有效重量(kg)" width="130">
            <template slot-scope="s">{{ num2(s.row.order_weight) }}</template>
          </el-table-column>

          <el-table-column label="实际确认重量(kg)" width="180">
            <template slot-scope="s">
              <el-input-number
                v-model="s.row.actual_weight"
                :disabled="!needManual(editPreview)"
                :min="0"
                :precision="2"
                :step="0.1"
                controls-position="right"
                style="width:140px;"
                @change="refreshEditDiff"
              />
            </template>
          </el-table-column>

          <el-table-column label="误差(kg)" width="100">
            <template slot-scope="s">
              <span :class="{ danger: Number(s.row.diff_weight || 0) !== 0 }">
                {{ num2(s.row.diff_weight) }}
              </span>
            </template>
          </el-table-column>

          <el-table-column prop="unaudited_order_count" label="未审核订单数" width="110" />
          <el-table-column label="未审核订单号" min-width="220">
            <template slot-scope="s">
              <span v-if="!s.row.unaudited_order_nos || !s.row.unaudited_order_nos.length">-</span>
              <el-tag
                v-for="no in s.row.unaudited_order_nos"
                :key="no"
                type="warning"
                size="mini"
                class="mb6 mr6"
              >
                {{ no }}
              </el-tag>
            </template>
          </el-table-column>
        </el-table>

        <div
          v-if="needManual(editPreview)"
          class="manual-summary"
        >
          <span>手工实际总和：{{ manualActualTotal }} kg</span>
          <span style="margin-left:20px;">过磅单重量：{{ num2(editPreview.weight) }} kg</span>
          <span
            style="margin-left:20px;"
            :class="{ danger: !manualTotalMatch }"
          >
            {{ manualTotalMatch ? '总和一致' : '总和不一致' }}
          </span>
        </div>
      </div>

      <span slot="footer">
        <el-button @click="editVisible=false">关闭</el-button>
        <el-button type="primary" @click="saveEditOnly">保存修改</el-button>
        <el-button
          v-if="editPreview && editPreview.has_unaudited_orders === false"
          type="success"
          :loading="approveLoading"
          @click="approveFromEdit"
        >
          通过审核
        </el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import {
  getWeighList,
  getWeighAuditPreview,
  auditWeigh
} from '@/api/weigh'

export default {
  name: 'WeighAudit',
  data() {
    return {
      loading: false,
      previewLoading: false,
      approveLoading: false,

      page: 1,
      pageSize: 20,
      total: 0,
      query: {
        keyword: '',
        audit_status: undefined
      },
      list: [],

      detailVisible: false,
      detailPreview: null,

      editVisible: false,
      editPreview: null,
      editForm: {
        remark: '',
        detail_items: []
      },

      draftMap: {}
    }
  },
  computed: {
    manualActualTotal() {
      return this.editForm.detail_items
        .reduce((sum, item) => sum + Number(item.actual_weight || 0), 0)
        .toFixed(2)
    },
    manualTotalMatch() {
      if (!this.editPreview) return true
      return Number(this.manualActualTotal) === Number(this.num2(this.editPreview.weight))
    },
    editAlertText() {
      if (!this.editPreview) return ''
      if (this.editPreview.has_unaudited_orders) {
        return '当前区间存在未审核订单，暂时不能通过。你可以先查看明细或修改备注，等订单审核完成后再通过。'
      }
      if (this.needManual(this.editPreview)) {
        return '误差比例大于 5%，请手工填写每个设备的实际重量，且总和必须等于过磅单实际重量。'
      }
      return '误差比例不超过 5%，系统可自动按实时有效订单重量通过。'
    }
  },
  mounted() {
    this.fetchList()
  },
  methods: {
    num2(v) {
      return Number(v || 0).toFixed(2)
    },
    garbageText(v) {
      const map = { 1: '废纸', 2: '塑料', 3: '衣物', 4: '废铁' }
      return map[v] || '-'
    },
    auditText(v) {
      const map = { 0: '待审核', 1: '已通过', 2: '已驳回' }
      return map[v] || '-'
    },
    auditTagType(v) {
      const map = { 0: 'warning', 1: 'success', 2: 'danger' }
      return map[v] || ''
    },
    formatTime(ts) {
      if (!ts) return '-'
      const d = new Date(Number(ts) * 1000)
      const pad = n => String(n).padStart(2, '0')
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`
    },
    getFinalDiffRatePercent(row) {
      return Number((Number(row.final_diff_rate || 0) * 100).toFixed(2))
    },
    needManual(row) {
      if (!row) return false
      return this.getFinalDiffRatePercent(row) > 5
    },

    reset() {
      this.query = {
        keyword: '',
        audit_status: undefined
      }
      this.page = 1
      this.fetchList()
    },

    normalizeListRow(row) {
      return {
        ...row,
        weigh_id: row.weigh_id || row.id,
        weigh_no: row.weigh_no || row.order_no || '-',
        vehicle_name: row.vehicle_name || row.car_name || '-',
        plate_no: row.plate_no || row.vehicle_plate || '-',
        garbage_type: row.garbage_type,
        garbage_name: row.garbage_name || this.garbageText(row.garbage_type),
        weight: row.weight,
        snapshot_room_weight: row.snapshot_room_weight || row.room_weight || 0,
        final_room_weight: row.final_room_weight || row.room_weight || 0,
        final_diff_rate: row.final_diff_rate || row.diff_rate || 0,
        audit_status: Number(row.audit_status || 0),
        has_unaudited_orders: row.has_unaudited_orders
      }
    },

    async fetchList() {
      this.loading = true
      try {
        const params = {
          page: this.page,
          page_size: this.pageSize
        }
        if (this.query.keyword) params.keyword = this.query.keyword.trim()
        if (this.query.audit_status === 0 || this.query.audit_status === 1 || this.query.audit_status === 2) {
          params.audit_status = this.query.audit_status
        }

        const res = await getWeighList(params)
        const items = res.items || res.data || res.rows || []
        this.list = items.map(this.normalizeListRow)
        this.total = res.total || items.length || 0
      } finally {
        this.loading = false
      }
    },

    async loadPreview(weighId) {
      this.previewLoading = true
      try {
        const res = await getWeighAuditPreview(weighId)
        return res
      } finally {
        this.previewLoading = false
      }
    },

    getDraft(weighId) {
      return this.draftMap[weighId] || null
    },

    setDraft(weighId, data) {
      this.$set(this.draftMap, weighId, JSON.parse(JSON.stringify(data)))
    },

    clearDraft(weighId) {
      this.$delete(this.draftMap, weighId)
    },

    buildEditFormFromPreview(preview) {
      const draft = this.getDraft(preview.weigh_id)

      if (draft) {
        this.editForm = JSON.parse(JSON.stringify(draft))
        this.refreshEditDiff()
        return
      }

      this.editForm = {
        remark: '',
        detail_items: (preview.detail_items || []).map(item => ({
          device_id: item.device_id,
          device_code: item.device_code,
          community_name: item.community_name,
          snapshot_weight: Number(item.snapshot_weight || 0),
          order_weight: Number(item.order_weight || 0),
          actual_weight: Number(item.actual_weight != null ? item.actual_weight : item.order_weight || 0),
          diff_weight: Number(item.diff_weight || 0),
          is_manual: Number(item.is_manual || 0),
          unaudited_order_count: Number(item.unaudited_order_count || 0),
          unaudited_order_nos: item.unaudited_order_nos || []
        }))
      }
      this.refreshEditDiff()
    },

    refreshEditDiff() {
      this.editForm.detail_items.forEach(item => {
        const actual = Number(item.actual_weight || 0)
        const order = Number(item.order_weight || 0)
        item.diff_weight = Number((actual - order).toFixed(2))
        item.is_manual = this.editPreview && this.needManual(this.editPreview) ? 1 : 0
      })
    },

    async openDetail(row) {
      this.detailVisible = true
      this.detailPreview = null
      try {
        const res = await this.loadPreview(row.weigh_id)
        this.detailPreview = res
      } catch (e) {
        this.detailVisible = false
      }
    },

    async openEdit(row) {
      this.editVisible = true
      this.editPreview = null
      try {
        const res = await this.loadPreview(row.weigh_id)
        this.editPreview = res
        this.buildEditFormFromPreview(res)
      } catch (e) {
        this.editVisible = false
      }
    },

    saveEditOnly() {
      if (!this.editPreview) return

      if (this.needManual(this.editPreview) && !this.manualTotalMatch) {
        this.$message.error(`手工填写的设备实际重量总和 ${this.manualActualTotal} 与过磅单重量 ${this.num2(this.editPreview.weight)} 不一致`)
        return
      }

      this.setDraft(this.editPreview.weigh_id, this.editForm)
      this.$message.success('修改已保存，接下来可以点击“通过审核”')
    },

    validateApprove(preview, form) {
      if (!preview) return false

      if (preview.has_unaudited_orders) {
        this.$alert(
          `${preview.msg || '当前过磅单时间区间内存在未审核订单，无法审核通过'}\n\n${(preview.unaudited_order_nos || []).join('，')}`,
          '无法通过',
          {
            type: 'warning',
            confirmButtonText: '知道了'
          }
        )
        return false
      }

      if (!this.needManual(preview)) {
        return true
      }

      const hasEmpty = form.detail_items.some(item =>
        item.actual_weight === null ||
        item.actual_weight === '' ||
        item.actual_weight === undefined
      )
      if (hasEmpty) {
        this.$message.error('误差超出允许范围，请手工填写每个设备的实际重量后再审核')
        return false
      }

      const total = Number(
        form.detail_items.reduce((sum, item) => sum + Number(item.actual_weight || 0), 0).toFixed(2)
      )
      const target = Number(this.num2(preview.weight))

      if (total !== target) {
        this.$message.error(`手工填写的设备实际重量总和 ${total.toFixed(2)} 与过磅单重量 ${target.toFixed(2)} 不一致`)
        return false
      }

      return true
    },

    buildApprovePayload(preview, form) {
      const payload = {
        action: 1,
        remark: form.remark || ''
      }

      if (this.needManual(preview)) {
        payload.detail_items = form.detail_items.map(item => ({
          device_id: item.device_id,
          device_code: item.device_code,
          community_name: item.community_name,
          order_weight: Number(item.order_weight || 0),
          actual_weight: Number(item.actual_weight || 0),
          diff_weight: Number(item.diff_weight || 0),
          is_manual: 1
        }))
      }

      return payload
    },

    async approveWithData(preview, form) {
      if (!this.validateApprove(preview, form)) return

      this.approveLoading = true
      try {
        const payload = this.buildApprovePayload(preview, form)
        const res = await auditWeigh(preview.weigh_id, payload)

        this.$alert(
          [
            `结果：${res.msg || '审核通过'}`,
            `过磅单号：${res.weigh_no || '-'}`,
            `最终理论重：${this.num2(res.final_room_weight)} kg`,
            `最终误差：${this.num2(res.final_diff_weight)} kg`,
            `最终误差率：${Number((Number(res.final_diff_rate || 0) * 100).toFixed(2))}%`,
            `是否人工调整：${Number(res.is_manual_adjust || 0) === 1 ? '是' : '否'}`
          ].join('\n'),
          '审核成功',
          { confirmButtonText: '确定' }
        )

        this.clearDraft(preview.weigh_id)
        this.editVisible = false
        this.fetchList()
      } catch (e) {
        const detail = e && e.response && e.response.data ? e.response.data.detail : null
        if (detail && typeof detail === 'object' && detail.msg) {
          this.$alert(
            `${detail.msg}\n\n${(detail.unaudited_order_nos || []).join('，')}`,
            '审核失败',
            {
              type: 'warning',
              confirmButtonText: '知道了'
            }
          )
        }
      } finally {
        this.approveLoading = false
      }
    },

    async approveFromEdit() {
      if (!this.editPreview) return
      this.setDraft(this.editPreview.weigh_id, this.editForm)
      await this.approveWithData(this.editPreview, this.editForm)
    },

    async quickApprove(row) {
      const preview = await this.loadPreview(row.weigh_id)
      const draft = this.getDraft(row.weigh_id)

      const form = draft
        ? JSON.parse(JSON.stringify(draft))
        : {
            remark: '',
            detail_items: (preview.detail_items || []).map(item => ({
              device_id: item.device_id,
              device_code: item.device_code,
              community_name: item.community_name,
              snapshot_weight: Number(item.snapshot_weight || 0),
              order_weight: Number(item.order_weight || 0),
              actual_weight: Number(item.actual_weight != null ? item.actual_weight : item.order_weight || 0),
              diff_weight: Number(item.diff_weight || 0),
              is_manual: Number(item.is_manual || 0),
              unaudited_order_count: Number(item.unaudited_order_count || 0),
              unaudited_order_nos: item.unaudited_order_nos || []
            }))
          }

      form.detail_items.forEach(item => {
        const actual = Number(item.actual_weight || 0)
        const order = Number(item.order_weight || 0)
        item.diff_weight = Number((actual - order).toFixed(2))
      })

      await this.approveWithData(preview, form)
    }
  }
}
</script>

<style scoped>
.page {
  padding: 12px;
  box-sizing: border-box;
  height: 100%;
  min-height: 0;
  display: flex;
}

.card {
  width: 100%;
  height: 100%;
  min-height: 0;
  display: flex;
  flex-direction: column;
}

::v-deep .card .el-card__body {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  padding: 16px;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.query-form {
  margin-bottom: 10px;
  flex-shrink: 0;
}

.table-wrap {
  flex: 1;
  min-height: 0;
  overflow: hidden;
}

.pager {
  margin-top: 12px;
  text-align: right;
  flex-shrink: 0;
}

.section-title {
  margin: 16px 0 10px;
  font-size: 15px;
  font-weight: 700;
}

.manual-summary {
  margin-top: 12px;
  font-size: 14px;
}

.danger {
  color: #f56c6c;
  font-weight: 700;
}

.warn-box {
  margin: 14px 0;
  padding: 12px;
  background: #fff7e6;
  border: 1px solid #f5dab1;
  border-radius: 6px;
}

.warn-title {
  color: #d46b08;
  font-weight: 700;
  margin-bottom: 8px;
}

.warn-tags {
  display: flex;
  flex-wrap: wrap;
}

.mr6 {
  margin-right: 6px;
}

.mb6 {
  margin-bottom: 6px;
}

.center-loading {
  height: 160px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #409EFF;
  gap: 8px;
}
</style>